import { Link as BaseLink } from "@netlify/ui";
import { forwardRef } from "react";

const isLinkInternal = (href: string): boolean => {
  try {
    const parsed = new URL(href);
    // FIXME(ndhoule): This is a little janky; the SDK should pass down the current app origin as context
    return (
      parsed.origin === "https://app.netlifystg.com" ||
      parsed.origin === "https://app.netlify.com" ||
      parsed.origin === "http://localhost:8888"
    );
  } catch {
    return false;
  }
};

/**
 * Detects if a link is internal, external, or neither (when href is `undefined`). If both external
 * and internal are true, preferences external.
 *
 * @internal
 */
const getLinkType = ({
  external,
  href,
  internal,
}: {
  external?: boolean | undefined;
  href: string | undefined;
  internal: boolean | undefined;
}): "external" | "internal" | "none" => {
  if (href === undefined) {
    return "none";
  }

  const isDetectedInternal = isLinkInternal(href);

  if (external === true) {
    return "external";
  }
  if (internal === true) {
    return "internal";
  }
  if (isDetectedInternal) {
    return "internal";
  }
  return "external";
};

export type LinkProps = {
  /**
   * Content to render within the element.
   */
  children: React.ReactNode;

  /**
   * A class property to attach to the element.
   *
   * @see {@link !Element.className}
   */
  className?: string | undefined;

  /**
   * Indicates whether or not the link references a URL outside of the Netlify app.
   *
   * @default true
   */
  external?: boolean | undefined;

  /**
   * Indicates whether or not the link references a URL inside of the Netlify app.
   *
   * @default false
   */
  internal?: boolean | undefined;

  /**
   * The URL that the `Link` points to.
   */
  href?: string | undefined;
};

/**
 * A component used to render URLs.
 *
 * In addition to applying styling your links, using this component ensures your URLs open in a new
 * window. For security reasons, the sandboxed iframe extensions run in restrict modifying the
 * current browsing context's URL; extensions can, however, open links in a new window. We recommend
 * using this component to ensure your links consistently work.
 *
 * @param props
 * @param ref
 */
// eslint-disable-next-line react/display-name
export const Link = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  LinkProps
>(({ children, href, internal, external, ...otherProps }, ref) => {
  const linkType = getLinkType({ href, internal, external });

  return (
    <BaseLink
      {...otherProps}
      action={linkType === "internal" ? true : undefined}
      external={linkType === "external" ? true : undefined}
      href={href}
      ref={ref}
      showExternalIcon={linkType === "external" ? true : undefined} // right-facing arrow, points diagonally upward
    >
      {children}
    </BaseLink>
  );
});
